
import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { Heart, Target, Compass, Users, TrendingUp, CheckCircle, ArrowRight, Lightbulb, BarChart3 } from 'lucide-react';

const StrategieMarquePage = () => {
  const processSteps = [
    {
      step: "01",
      title: "Audit de Marque",
      description: "Analyse complète de votre positionnement actuel et de votre écosystème concurrentiel",
      icon: BarChart3
    },
    {
      step: "02", 
      title: "Définition des Objectifs",
      description: "Clarification de votre vision, mission et objectifs stratégiques",
      icon: Target
    },
    {
      step: "03",
      title: "Positionnement Unique",
      description: "Création d'un territoire de marque différenciant et mémorable",
      icon: Compass
    },
    {
      step: "04",
      title: "Architecture de Marque",
      description: "Structuration cohérente de votre écosystème de communication",
      icon: Lightbulb
    }
  ];

  const services = [
    {
      title: "Audit de Marque",
      description: "Diagnostic complet de votre identité actuelle",
      features: ["Analyse concurrentielle", "Audit de perception", "Étude de positionnement", "Recommandations stratégiques"]
    },
    {
      title: "Positionnement",
      description: "Définition de votre place unique sur le marché",
      features: ["Territoire de marque", "Proposition de valeur", "Avantages concurrentiels", "Promesse de marque"]
    },
    {
      title: "Plateforme de Marque",
      description: "Construction de votre ADN communicationnel",
      features: ["Vision & Mission", "Valeurs", "Personnalité", "Ton de voix"]
    },
    {
      title: "Stratégie Déploiement",
      description: "Plan d'action pour incarner votre marque",
      features: ["Roadmap", "Points de contact", "Mesure performance", "Optimisation continue"]
    }
  ];

  const benefices = [
    {
      icon: Target,
      title: "Différenciation claire",
      description: "Une position unique qui vous distingue de vos concurrents"
    },
    {
      icon: Users,
      title: "Connexion émotionnelle",
      description: "Une marque qui résonne avec votre audience cible"
    },
    {
      icon: TrendingUp,
      title: "Croissance durable",
      description: "Des fondations solides pour votre développement"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-navy-50 to-pink-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/80 text-blue-600 px-6 py-3 rounded-full text-sm font-medium mb-8">
              <Heart size={16} />
              Stratégie de Marque
            </div>
            
            <h1 className="text-6xl md:text-7xl font-light text-navy-900 mb-8 leading-tight">
              Révélez votre <span className="font-semibold text-blue-600">essence</span>
              <br />
              <span className="text-4xl md:text-5xl text-navy-600">et conquérez vos marchés</span>
            </h1>
            <p className="text-xl text-navy-600 mb-12 font-light leading-relaxed max-w-3xl mx-auto">
              Je vous accompagne dans la construction d'une stratégie de marque forte et différenciante. 
              Ensemble, révélons ce qui vous rend unique et créons une connexion durable avec votre audience.
            </p>
          </div>
        </div>
      </section>

      {/* Qu'est-ce que la stratégie de marque */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-4xl font-light text-navy-900 mb-6">
                  Qu'est-ce qu'une <span className="font-semibold text-blue-600">stratégie de marque</span> ?
                </h2>
                <p className="text-lg text-navy-600 font-light mb-6 leading-relaxed">
                  La stratégie de marque, c'est l'art de définir qui vous êtes, pourquoi vous existez et comment vous voulez être perçu. 
                  C'est la boussole qui guide toutes vos actions de communication et vous aide à créer une relation authentique avec votre public.
                </p>
                <p className="text-lg text-navy-600 font-light mb-8 leading-relaxed">
                  Une stratégie de marque réussie transforme votre entreprise en une marque désirable, mémorable et différenciante.
                </p>
                <ul className="space-y-3">
                  {["Positionnement unique sur le marché", "Cohérence sur tous les points de contact", "Différenciation claire de la concurrence", "Connexion émotionnelle avec l'audience"].map((item, index) => (
                    <li key={index} className="flex items-center gap-3 text-navy-600">
                      <CheckCircle className="text-blue-600 flex-shrink-0" size={20} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-pink-50 p-12">
                <div className="text-center">
                  <Heart className="text-blue-600 mx-auto mb-6" size={64} />
                  <h3 className="text-2xl font-semibold text-navy-900 mb-4">L'essence de votre marque</h3>
                  <p className="text-navy-600 font-light">
                    "Une marque n'est pas ce que vous dites qu'elle est. C'est ce que vos clients disent qu'elle est."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-navy-50/30">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-light text-navy-900 mb-6">
                Mes <span className="font-semibold text-blue-600">services</span>
              </h2>
              <p className="text-lg text-navy-600 font-light max-w-2xl mx-auto">
                Une approche complète pour construire une stratégie de marque impactante
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {services.map((service, index) => (
                <div key={index} className="bg-white p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <h3 className="text-xl font-semibold text-navy-900 mb-4">{service.title}</h3>
                  <p className="text-navy-600 font-light mb-6">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center gap-2 text-sm text-navy-600">
                        <CheckCircle size={12} className="text-blue-600 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Processus */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-light text-navy-900 mb-6">
                Ma <span className="font-semibold text-blue-600">méthodologie</span>
              </h2>
              <p className="text-lg text-navy-600 font-light max-w-2xl mx-auto">
                Un processus structuré pour révéler et construire votre marque
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {processSteps.map((step, index) => (
                <div key={index} className="text-center group">
                  <div className="bg-blue-600 text-white p-6 w-20 h-20 mx-auto mb-6 flex items-center justify-center text-2xl font-bold group-hover:bg-pink-500 transition-colors duration-300">
                    {step.step}
                  </div>
                  <step.icon className="text-blue-600 mx-auto mb-4 group-hover:text-pink-500 transition-colors duration-300" size={32} />
                  <h3 className="text-lg font-semibold text-navy-900 mb-3">{step.title}</h3>
                  <p className="text-navy-600 font-light text-sm leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Bénéfices */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-navy-900">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-light text-white mb-6">
              Les bénéfices d'une <span className="font-semibold text-pink-400">stratégie claire</span>
            </h2>
            <p className="text-xl text-navy-200 mb-12 font-light">
              Pourquoi investir dans votre stratégie de marque
            </p>
            
            <div className="grid md:grid-cols-3 gap-8">
              {benefices.map((benefice, index) => (
                <div key={index} className="text-center">
                  <div className="bg-white/10 p-6 w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                    <benefice.icon className="text-pink-400" size={32} />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{benefice.title}</h3>
                  <p className="text-navy-200 font-light">{benefice.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-navy-50/50">
        <div className="container mx-auto px-6 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl font-light text-navy-900 mb-6">
              Prêt à révéler votre <span className="font-semibold text-blue-600">potentiel</span> ?
            </h2>
            <p className="text-xl text-navy-600 mb-8 font-light">
              Construisons ensemble une stratégie de marque qui vous propulse vers le succès.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="mailto:julycia.pro@gmail.com"
                className="bg-blue-600 text-white px-8 py-4 font-medium hover:bg-blue-700 transition-colors duration-300 group inline-flex items-center justify-center gap-2"
              >
                Démarrer ma stratégie
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
              </a>
              <button 
                onClick={() => {
                  const phoneNumber = "+33643581540";
                  const message = "Bonjour Julycia ! J'aimerais discuter d'un projet de stratégie de marque avec vous.";
                  const whatsappUrl = `https://wa.me/${phoneNumber.replace(/\D/g, '')}?text=${encodeURIComponent(message)}`;
                  window.open(whatsappUrl, '_blank');
                }}
                className="bg-green-500 text-white px-8 py-4 font-medium hover:bg-green-600 transition-colors duration-300 group inline-flex items-center justify-center gap-2"
              >
                Discuter sur WhatsApp
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default StrategieMarquePage;
